import { Injectable, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Injectable({
    providedIn: 'root',
})
export class HtmlUtils {
    private domSanitizer = inject(DomSanitizer);

    /**
     * Sanitize HTML content using Angular's built-in DomSanitizer
     * @param html - The HTML string to sanitize
     * @returns Sanitized HTML string
     */
    sanitizeHtml(html: string): string {
        if (!html || html.trim() === '') {
            return '';
        }

        // Use Angular's DomSanitizer to clean HTML (SecurityContext.HTML = 1)
        const sanitized = this.domSanitizer.sanitize(1, html);
        return sanitized || '';
    }

    /**
     * Sanitize HTML content with more lenient rules for rich text editing
     * This preserves Quill formatting classes and attributes that are safe
     * @param html - The HTML string to sanitize
     * @returns Sanitized HTML string with preserved formatting
     */
    sanitizeRichTextHtml(html: string): string {
        if (!html || html.trim() === '') {
            return '';
        }

        // For rich text content, we'll use a more permissive approach
        // since Angular's DomSanitizer can be too restrictive for Quill content
        return this.customSanitizeForQuill(html);
    }

    /**
     * Sanitize stored Quill HTML for read-only display.
     * Keeps Quill's code-block and list markup so the editor can draw it,
     * and drops scripts, event handlers, and unsafe URLs first.
     */
    sanitizeQuillHtml(html: string): string {
        if (!html || html.trim() === '') {
            return '';
        }

        return this.cleanQuillHtml(this.decodeEncodedMarkup(html), false);
    }

    /**
     * Custom sanitization specifically designed for Quill editor content
     * @param html - Raw HTML from Quill editor
     * @returns Sanitized HTML with Quill formatting preserved
     */
    private customSanitizeForQuill(html: string): string {
        return this.cleanQuillHtml(html, true);
    }

    /**
     * Parse with DOMParser so scripts do not run, then drop unsafe nodes.
     * normalize converts Quill lists and code blocks for a static innerHTML host.
     */
    private cleanQuillHtml(html: string, normalize: boolean): string {
        const container = new DOMParser().parseFromString(html, 'text/html').body;

        if (normalize) {
            this.normalizeQuillLists(container);
            this.normalizeQuillCodeBlocks(container);
        }

        this.removeUnsafeElements(container);
        this.removeUnsafeAttributes(container);

        return container.innerHTML;
    }

    /**
     * Some stored values are entity-encoded markup (&lt;p&gt;).
     * Decode that once so the sanitizer sees the real tags.
     * Leave strings that already contain elements unchanged.
     */
    private decodeEncodedMarkup(html: string): string {
        if (!html.includes('&lt;')) {
            return html;
        }

        const body = new DOMParser().parseFromString(html, 'text/html').body;
        const hasElements = Array.from(body.childNodes).some((node) => node.nodeType === Node.ELEMENT_NODE);
        if (hasElements) {
            return html;
        }

        return body.textContent || html;
    }

    /**
     * Quill stores every list as an ol and marks each item with data-list.
     * Split those into ul and ol so bullets and numbers render outside the editor.
     */
    private normalizeQuillLists(container: Element): void {
        const lists = Array.from(container.querySelectorAll('ol, ul')).reverse();
        lists.forEach((list) => this.splitQuillList(list));
    }

    private splitQuillList(list: Element): void {
        const parent = list.parentNode;
        if (!parent) {
            return;
        }

        const items = Array.from(list.children).filter((child) => child.tagName === 'LI');
        if (!items.some((item) => item.hasAttribute('data-list'))) {
            items.forEach((item) => this.removeQuillUi(item));
            return;
        }

        let current: HTMLElement | null = null;
        let currentType: string | null = null;
        const groups: HTMLElement[] = [];

        items.forEach((item) => {
            const listType = (item.getAttribute('data-list') || '').toLowerCase();
            const type = listType === 'bullet' || listType === 'checked' ? 'ul' : 'ol';

            this.removeQuillUi(item);
            item.removeAttribute('data-list');

            if (!current || type !== currentType) {
                current = document.createElement(type);
                groups.push(current);
                currentType = type;
            }

            current.appendChild(item);
        });

        groups.forEach((group) => parent.insertBefore(group, list));
        list.remove();
    }

    private removeQuillUi(listItem: Element): void {
        listItem.querySelectorAll('span').forEach((span) => {
            const empty = !(span.textContent || '').trim() && span.children.length === 0;
            const isUi =
                span.classList.contains('ql-ui') ||
                span.getAttribute('contenteditable') === 'false' ||
                span.attributes.length === 0;

            if (empty && isUi) {
                span.remove();
            }
        });
    }

    /**
     * Quill code blocks are one div per line. Join them into a pre/code block.
     */
    private normalizeQuillCodeBlocks(container: Element): void {
        const blocks = Array.from(container.querySelectorAll('.ql-code-block'));
        const seen = new Set<Element>();

        blocks.forEach((block) => {
            if (seen.has(block) || !block.isConnected) {
                return;
            }

            const group: Element[] = [];
            let current: Element | null = block;
            while (current?.classList.contains('ql-code-block')) {
                seen.add(current);
                group.push(current);
                current = current.nextElementSibling;
            }

            const pre = document.createElement('pre');
            const code = document.createElement('code');
            code.textContent = group.map((line) => line.textContent ?? '').join('\n');
            pre.appendChild(code);
            group[0].replaceWith(pre);
            group.slice(1).forEach((line) => line.remove());
        });

        container.querySelectorAll('.ql-code-block-container').forEach((wrapper) => {
            const parent = wrapper.parentNode;
            if (!parent) {
                return;
            }

            while (wrapper.firstChild) {
                parent.insertBefore(wrapper.firstChild, wrapper);
            }
            wrapper.remove();
        });
    }

    /**
     * Remove unsafe HTML elements
     * @param container - DOM container to clean
     */
    private removeUnsafeElements(container: Element): void {
        const unsafeTags = ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button'];

        unsafeTags.forEach((tag) => {
            const elements = container.querySelectorAll(tag);
            elements.forEach((el) => el.remove());
        });
    }

    /**
     * Remove unsafe attributes while preserving Quill formatting
     * @param container - DOM container to clean
     */
    private removeUnsafeAttributes(container: Element): void {
        const allElements = container.querySelectorAll('*');

        allElements.forEach((element) => {
            // Get all attributes
            const attributes = Array.from(element.attributes);

            attributes.forEach((attr) => {
                const name = attr.name.toLowerCase();
                const value = attr.value;

                // Remove event handlers
                if (name.startsWith('on')) {
                    element.removeAttribute(name);
                    return;
                }

                // Remove javascript: and data: URLs
                if (
                    (name === 'href' || name === 'src') &&
                    (value.toLowerCase().includes('javascript:') || value.toLowerCase().includes('data:'))
                ) {
                    element.removeAttribute(name);
                    return;
                }

                // Remove style attributes (inline styles can be dangerous)
                if (name === 'style') {
                    element.removeAttribute(name);
                    return;
                }

                // Quill uses this to tell bullets from numbered items
                if (name === 'data-list') {
                    const listType = value.toLowerCase();
                    if (
                        element.tagName === 'LI' &&
                        ['bullet', 'ordered', 'checked', 'unchecked'].includes(listType)
                    ) {
                        element.setAttribute(name, listType);
                    } else {
                        element.removeAttribute(name);
                    }
                    return;
                }

                // Preserve safe class attributes (especially Quill classes)
                if (name === 'class') {
                    const safeClasses = this.filterSafeClasses(value);
                    if (safeClasses) {
                        element.setAttribute('class', safeClasses);
                    } else {
                        element.removeAttribute('class');
                    }
                    return;
                }

                // Keep other generally safe attributes
                const safeAttributes = [
                    'id',
                    'title',
                    'alt',
                    'href',
                    'src',
                    'target',
                    'rel',
                    'width',
                    'height',
                    'colspan',
                    'rowspan',
                ];

                if (!safeAttributes.includes(name)) {
                    element.removeAttribute(name);
                }
            });
        });
    }

    /**
     * Filter CSS classes to only allow safe ones
     * @param classString - Space-separated class names
     * @returns Filtered class string or empty string
     */
    private filterSafeClasses(classString: string): string {
        const classes = classString
            .split(' ')
            .map((c) => c.trim())
            .filter((c) => c);
        const safeClasses: string[] = [];

        classes.forEach((className) => {
            // Allow Quill-specific classes
            if (
                /^ql-(indent-\d+|align-\w+|direction-\w+|size-\w+|font-\w+|code-block-container|code-block|ui|formula|syntax)$/.test(
                    className,
                )
            ) {
                safeClasses.push(className);
            }
            // Allow common semantic classes
            else if (
                /^(text-\w+|font-\w+|list-\w+|bg-\w+|border-\w+|p-\d+|m-\d+|mt-\d+|mb-\d+|ml-\d+|mr-\d+)$/.test(
                    className,
                )
            ) {
                safeClasses.push(className);
            }
        });

        return safeClasses.join(' ');
    }

    /**
     * Check if HTML content is empty (contains only empty tags or whitespace)
     * @param html - The HTML string to check
     * @returns True if content is effectively empty
     */
    isHtmlEmpty(html: string): boolean {
        if (!html || html.trim() === '') {
            return true;
        }

        // Create temporary div to extract text content
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html.trim();
        const textContent = tempDiv.textContent || tempDiv.innerText || '';

        return textContent.trim().length === 0;
    }

    /**
     * Check if HTML content appears to be from Quill editor
     * @param html - The HTML string to check
     * @returns True if content has Quill-specific patterns
     */
    isQuillContent(html: string): boolean {
        if (!html) return false;

        // Look for Quill-specific patterns
        return (
            html.includes('ql-') ||
            html.includes('<ol>') ||
            html.includes('<ul>') ||
            html.includes('ql-indent') ||
            html.includes('ql-align')
        );
    }

    /**
     * Preserve Quill's exact list structure
     * @param html - HTML content with potential list formatting
     * @returns HTML with preserved Quill list structure
     */
    preserveQuillListStructure(html: string): string {
        if (!html || !this.isQuillContent(html)) {
            return html;
        }

        // Quill generates lists in a specific way - preserve the exact structure
        // This is especially important for nested lists and indentation
        return html; // For now, return as-is since minimal sanitization preserves structure
    }
    /**
     * Extract plain text from HTML content
     * @param html - The HTML string to extract text from
     * @returns Plain text content
     */
    extractTextFromHtml(html: string): string {
        if (!html || html.trim() === '') {
            return '';
        }

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        return (tempDiv.textContent || tempDiv.innerText || '').trim();
    }

    /**
     * Process Quill editor content for saving
     * @param htmlContent - Raw HTML content from Quill
     * @returns Processed and sanitized HTML or empty string
     */
    processQuillContent(htmlContent: string): string {
        if (!htmlContent) {
            return '';
        }

        const trimmedContent = htmlContent.trim();

        // Check if content is effectively empty
        if (this.isHtmlEmpty(trimmedContent)) {
            return '';
        }

        // For Quill content, use minimal sanitization to preserve exact formatting
        return this.minimalSanitizeForQuill(trimmedContent);
    }

    /**
     * Minimal sanitization that preserves Quill's exact HTML structure
     * Only removes truly dangerous elements while keeping all formatting intact
     * @param html - Raw HTML from Quill editor
     * @returns Minimally sanitized HTML with all Quill formatting preserved
     */
    private minimalSanitizeForQuill(html: string): string {
        const originalHasLists = html.includes('<ol>') || html.includes('<ul>');

        // Create a temporary DOM element to parse the HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        // Only remove truly dangerous elements
        this.removeOnlyDangerousElements(tempDiv);
        this.removeOnlyDangerousAttributes(tempDiv);

        const result = tempDiv.innerHTML;

        // Debug logging if lists were present
        if (originalHasLists) {
            const resultHasLists = result.includes('<ol>') || result.includes('<ul>');
            console.log('Minimal sanitization result:', {
                hadListsBefore: originalHasLists,
                hasListsAfter: resultHasLists,
                originalLength: html.length,
                resultLength: result.length,
                listsPreserved: originalHasLists === resultHasLists,
                sampleBefore: html.substring(0, 200),
                sampleAfter: result.substring(0, 200),
            });
        }

        return result;
    }

    /**
     * Remove only truly dangerous HTML elements (minimal removal)
     * @param container - DOM container to clean
     */
    private removeOnlyDangerousElements(container: Element): void {
        // Only remove elements that can execute code or load external resources
        const dangerousTags = [
            'script',
            'iframe',
            'object',
            'embed',
            'form',
            'input',
            'button',
            'meta',
            'link',
            'style',
        ];

        dangerousTags.forEach((tag) => {
            const elements = container.querySelectorAll(tag);
            elements.forEach((el) => el.remove());
        });
    }

    /**
     * Remove only truly dangerous attributes (minimal removal)
     * @param container - DOM container to clean
     */
    private removeOnlyDangerousAttributes(container: Element): void {
        const allElements = container.querySelectorAll('*');

        allElements.forEach((element) => {
            // Get all attributes
            const attributes = Array.from(element.attributes);

            attributes.forEach((attr) => {
                const name = attr.name.toLowerCase();
                const value = attr.value.toLowerCase();

                // Remove event handlers
                if (name.startsWith('on')) {
                    element.removeAttribute(name);
                    return;
                }

                // Remove javascript: URLs
                if ((name === 'href' || name === 'src') && value.includes('javascript:')) {
                    element.removeAttribute(name);
                    return;
                }

                // Keep everything else - including class, style, data attributes that Quill might use
            });
        });
    }
    /**
     * Create a SafeHtml object for template binding
     * @param html - The HTML string to make safe
     * @returns SafeHtml object for Angular templates
     */
    createSafeHtml(html: string): SafeHtml {
        return this.domSanitizer.bypassSecurityTrustHtml(html);
    }

    /**
     * Create a SafeHtml object specifically for rich text content display
     * This first sanitizes the content then marks it as safe for display
     * @param html - Rich text HTML content
     * @returns SafeHtml object for Angular templates
     */
    createSafeRichTextHtml(html: string): SafeHtml {
        const sanitizedHtml = this.sanitizeRichTextHtml(html);
        return this.domSanitizer.bypassSecurityTrustHtml(sanitizedHtml);
    }

    /**
     * Format file size in human-readable format
     * @param bytes - File size in bytes
     * @returns Formatted file size string
     */
    formatFileSize(bytes: number): string {
        if (bytes === 0) return '0 Bytes';

        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    /**
     * Validate and format different value types for system configuration
     * @param value - The value to format
     * @param type - The configuration type
     * @returns Formatted value ready for storage
     */
    formatConfigValue(value: any, type: string): string {
        switch (type) {
            case 'RICHTEXT':
                return this.processQuillContent(value);

            case 'BOOLEAN':
                return value === 'true' || value === true ? 'true' : 'false';

            case 'NUMBER':
                const numValue = parseFloat(value);
                return isNaN(numValue) ? '0' : numValue.toString();

            case 'FILE':
                if (value instanceof File) {
                    const fileData = {
                        name: value.name,
                        size: value.size,
                        type: value.type,
                        lastModified: value.lastModified,
                    };
                    return JSON.stringify(fileData);
                }
                return value || '';

            default:
                return (value || '').toString();
        }
    }

    /**
     * Prepare stored HTML content for loading back into Quill editor
     * This ensures the content maintains proper list formatting when loaded
     * @param html - Stored HTML content
     * @returns HTML ready for Quill editor
     */
    prepareContentForQuill(html: string): string {
        if (!html || html.trim() === '') {
            return '';
        }

        const content = html.trim();

        // Debug logging for list formatting issues
        if (content.includes('<ol>') || content.includes('<ul>')) {
            console.log('Loading Quill content with lists:', {
                originalContent: content,
                hasOrderedList: content.includes('<ol>'),
                hasUnorderedList: content.includes('<ul>'),
                listItems: content.match(/<li[^>]*>/g)?.length || 0,
            });
        }

        // For content being loaded back into Quill, we want to preserve everything exactly
        // Quill will handle its own sanitization and formatting
        return content;
    }

    /**
     * Get Quill content in different formats
     * @param format - The desired format ('html', 'text', 'delta', 'combined')
     * @returns Content in the specified format
     */
    getQuillContent(format: 'html' | 'delta' | 'text' | 'combined' = 'html'): any {
        const quillContainer = document.querySelector('.ql-container');
        if (!quillContainer || !(quillContainer as any).__quill) {
            return format === 'html' ? '' : format === 'text' ? '' : format === 'delta' ? null : {};
        }

        const quill = (quillContainer as any).__quill;

        switch (format) {
            case 'html':
                return quill.root.innerHTML === '<p><br></p>' ? '' : quill.root.innerHTML;

            case 'delta':
                return quill.getContents();

            case 'text':
                return quill.getText().trim();

            case 'combined':
                const html = quill.root.innerHTML === '<p><br></p>' ? '' : quill.root.innerHTML;
                const text = quill.getText().trim();
                const delta = quill.getContents();

                return {
                    html: html,
                    text: text,
                    delta: delta,
                    length: quill.getLength(),
                    isEmpty: text.length === 0,
                    lastModified: new Date().toISOString(),
                };

            default:
                return '';
        }
    }

    /**
     * Scroll to top of page or element with smooth behavior
     * Tries multiple scroll strategies to ensure it works across different layouts
     * @param element - Optional element to scroll into view. If not provided, scrolls page to top
     */
    scrollToTop(element?: HTMLElement): void {
        // Use setTimeout to ensure DOM has updated
        setTimeout(() => {
            if (element) {
                // Scroll specific element into view
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }

            // Also try window scroll as fallback
            window.scrollTo({ top: 0, behavior: 'smooth' });

            // Try scrolling document element
            document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
        }, 0);
    }

    /**
     * Sanitizes HTML content for safe display in toasts/notifications.
     * Only allows safe formatting tags and strips all dangerous elements and attributes.
     * @param html - The HTML string to sanitize
     * @returns SafeHtml that can be used with [innerHTML]
     */
    /**
     * Keep bold, italic, underline, and line breaks. Attributes are removed.
     */
    sanitizeInlineFormatting(html: string): string {
        if (!html || html.trim() === '') {
            return '';
        }

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const allowedTags = ['b', 'strong', 'em', 'i', 'u', 'br'];
        const droppedTags = ['script', 'style', 'iframe', 'object', 'embed', 'noscript', 'template', 'svg', 'math'];

        const cleanNode = (node: Node): Node | null => {
            if (node.nodeType === Node.TEXT_NODE) {
                return node.cloneNode();
            }

            if (node.nodeType !== Node.ELEMENT_NODE) {
                return null;
            }

            const element = node as Element;
            const tagName = element.tagName.toLowerCase();
            if (droppedTags.includes(tagName)) {
                return null;
            }

            const fragment = document.createDocumentFragment();

            element.childNodes.forEach((child) => {
                const cleaned = cleanNode(child);
                if (cleaned) {
                    fragment.appendChild(cleaned);
                }
            });

            if (!allowedTags.includes(tagName)) {
                return fragment;
            }

            const cleanedElement = document.createElement(tagName);
            cleanedElement.appendChild(fragment);
            return cleanedElement;
        };

        const result = document.createDocumentFragment();
        doc.body.childNodes.forEach((node) => {
            const cleaned = cleanNode(node);
            if (cleaned) {
                result.appendChild(cleaned);
            }
        });

        const container = document.createElement('div');
        container.appendChild(result);
        return container.innerHTML;
    }

    sanitizeForToast(html: string): SafeHtml {
        return this.domSanitizer.bypassSecurityTrustHtml(this.stripDangerousTags(html));
    }

    /**
     * Strips dangerous HTML tags and attributes, keeping only safe formatting tags.
     * Allows: b, i, strong, em, u, br, span, a (with safe href), p, ul, ol, li
     * @param html - Raw HTML string
     * @returns Cleaned HTML string
     */
    stripDangerousTags(html: string): string {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const allowedTags = ['b', 'i', 'strong', 'em', 'u', 'br', 'span', 'a', 'p', 'ul', 'ol', 'li'];
        const allowedAttributes: Record<string, string[]> = {
            a: ['href', 'target', 'rel'],
            span: ['class'],
        };

        const cleanNode = (node: Node): Node | null => {
            if (node.nodeType === Node.TEXT_NODE) {
                return node.cloneNode();
            }
            if (node.nodeType === Node.ELEMENT_NODE) {
                const el = node as Element;
                const tagName = el.tagName.toLowerCase();

                if (!allowedTags.includes(tagName)) {
                    // Replace disallowed element with its children
                    const fragment = document.createDocumentFragment();
                    el.childNodes.forEach((child) => {
                        const cleaned = cleanNode(child);
                        if (cleaned) fragment.appendChild(cleaned);
                    });
                    return fragment;
                }

                // Clone allowed element
                const newEl = document.createElement(tagName);
                const allowed = allowedAttributes[tagName] || [];
                allowed.forEach((attr) => {
                    const value = el.getAttribute(attr);
                    if (value !== null) {
                        // Extra safety for href - only allow safe protocols
                        if (attr === 'href' && !/^(https?:|mailto:|#)/i.test(value)) {
                            return;
                        }
                        newEl.setAttribute(attr, value);
                    }
                });

                // Add rel="noopener noreferrer" to external links
                if (tagName === 'a' && newEl.getAttribute('target') === '_blank') {
                    newEl.setAttribute('rel', 'noopener noreferrer');
                }

                el.childNodes.forEach((child) => {
                    const cleaned = cleanNode(child);
                    if (cleaned) newEl.appendChild(cleaned);
                });
                return newEl;
            }
            return null;
        };

        const result = document.createDocumentFragment();
        doc.body.childNodes.forEach((node) => {
            const cleaned = cleanNode(node);
            if (cleaned) result.appendChild(cleaned);
        });

        const div = document.createElement('div');
        div.appendChild(result);
        return div.innerHTML;
    }
}
